import { z } from "zod";

import { type BoostReason } from "@/features/boosts/utils/boost-reason-codes";

// Wire shape is a subset of `ApiErrorReason` (`@/types/api`): boost eligibility
// reasons omit `error_field` and may omit `error_message` on the eligibility
// 200 response. We accept any string for `error_code` so an unknown future
// code does not throw the schema; `BoostReason.errorCode` is therefore typed
// as `string` and consumers narrow with `isBoostReasonCode` before indexing
// typed lookups.
const rawReasonSchema = z.object({
  error_code: z.string(),
  error_message: z.string().nullish(),
});

// TODO(TMT-XXX): remove legacy-string branch once BE !3525 is fully deployed
// and no node returns prose-only reasons. The union exists only for
// rolling-deploy safety; codes-only is the post-deploy steady state.
//
// Backend may send legacy prose strings (during rolling deploy) OR typed
// objects. Legacy strings coerce to a BoostReason whose `errorCode` is the
// raw prose; downstream `formatBoostReason` falls through to the localized
// generic fallback because `isBoostReasonCode` rejects prose. This keeps the
// disabled toggle visible during the deploy window instead of silently
// flipping to enabled when an entire reasons array is dropped.
const reasonSchema = z
  .union([rawReasonSchema, z.string()])
  .transform((entry): BoostReason => {
    if (typeof entry === "string") {
      return { errorCode: entry, errorMessage: entry };
    }
    return {
      errorCode: entry.error_code,
      errorMessage: entry.error_message ?? entry.error_code,
    };
  });

const boostEligibilitySchema = z
  .object({
    eligible: z.coerce.boolean().catch(false),
    reasons: z.array(reasonSchema).default([]),
    // Dollars, as a scale-pinned string ("9.99"): the backend renders money
    // with format('%.2f', ...) so a whole-dollar amount keeps both decimal
    // places, which a raw BigDecimal ("10.0") would drop. Coerced to a number
    // here because Intl.NumberFormat takes a number, and every downstream
    // consumer formats rather than does arithmetic — all totals are computed
    // server-side and arrive via /quote_tax.
    //
    // Explicitly null on the ineligible response, which is a 200 with
    // `eligible: false` — not an error status. EligibilityService returns a
    // full nil-filled struct so the payload shape is identical either way.
    // `.nullable()` short-circuits before coercion runs, so null stays null
    // rather than becoming Number(null) === 0.
    amount: z.coerce.number().nullable().catch(null),
    tax_quote_token: z.string().nullable().catch(null),
    impression_cap: z.number().nullable().catch(null),
    duration_hours: z.number().nullable().catch(null),
    utilization_pct: z.number().nullable().catch(null),
    multiplier: z.number().nullable().catch(null),
    min_account_age_days: z.number().int().positive().nullable().catch(null),
  })
  .transform((input) => ({
    eligible: input.eligible,
    reasons: input.reasons,
    amount: input.amount,
    taxQuoteToken: input.tax_quote_token,
    // Backend sends null for stats fields when ineligible; UI only reads them
    // behind an eligibility guard, so fall back to config-default shape.
    impressionCap: input.impression_cap ?? 1000,
    durationHours: input.duration_hours ?? 24,
    utilizationPct: input.utilization_pct ?? 0,
    multiplier: input.multiplier ?? 1,
    minAccountAgeDays: input.min_account_age_days,
    fetchedAt: Date.now(),
  }));

type BoostEligibility = z.infer<typeof boostEligibilitySchema>;

export { boostEligibilitySchema, type BoostEligibility };
